import { showErrorMessage } from '@acorn-portal/shared/src/components/toast/messageUtils';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import { PrivateCarClient } from '@acorn-portal/shared/src/services/subscriptionAPI';
import { resetDrivers, setDriverData } from '@acorn-portal/shared/src/store/subscription-amend-drivers.slice';
import { setSubscriptionData } from '@acorn-portal/shared/src/store/subscription.slice';
import { updateChangeOfDriver } from '@acorn-portal/shared/src/store/subscriptionChanges.slice';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { validateDriverData } from '../../validation/amend-drivers-page-validation';
import { prepareQuoteCommand } from '../utils/prepare-quote-command';

export const useSubmitDriversPage = () => {
	const [submitting, setSubmitting] = useState(false);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { quoteData, policyToken, effectiveDate } = useAppSelector((state) => state.subscription);
	const driverStoreData = useAppSelector((store) => store.subscriptionAmendDrivers.data);

	const handleDriverPageSubmit = async () => {
		const workingQuoteData = structuredClone(quoteData);
		const workingDriverData = structuredClone(driverStoreData);
		const originalDriverData = quoteData?.quoteDetails?.riskData?.driverDetails ?? [];
		// Page level validation
		const { isValid, message } = validateDriverData(workingDriverData, originalDriverData);
		if (!isValid && message) {
			showErrorMessage(message);
			return;
		}
		try {
			setSubmitting(true);
			// Prepare command
			const quoteReference = quoteData?.quoteResponse?.quoteRef ?? '';
			const quoteToken = quoteData?.quoteToken ?? '';
			const command = prepareQuoteCommand(quoteReference, quoteToken, workingDriverData, workingQuoteData);
			// Submit updated drivers
			// TODO: investigate a reusable redux thunk once all flows are ready
			const url = import.meta.env.VITE_SUBSCRIPTION_API;
			const subscriptionClient = new PrivateCarClient(url, API);
			const response = await subscriptionClient.updateQuote(command);
			const quoted = response?.quoteResponse?.underwrittenOptions?.[0].status === 'Quoted';
			if (quoted) {
				// Update the store with the response and navigate to the summary page
				dispatch(
					setSubscriptionData({
						policyToken: policyToken ?? '',
						effectiveDate: effectiveDate ?? '',
						sendWelcomePackStatus: undefined,
						autoRenew: undefined,
						quoteData: response,
					}),
				);

				const driverDetails = response?.quoteDetails?.riskData?.driverDetails;
				// Reset the driver store data for any further visits to the amend drivers page
				if (driverDetails) {
					const freshStoreData = driverDetails;
					dispatch(setDriverData(freshStoreData));
				} else {
					dispatch(resetDrivers());
				}
				// Show change of driver info on the summary page (green border), then navigate there
				dispatch(updateChangeOfDriver(true));
				navigate(`${ROUTE_PATHS.subscriptionRestartSummary}/${policyToken}`);
			} else {
				// If unsuccessful, navigate to the referral page
				dispatch(setDriverData(originalDriverData));
				navigate(ROUTE_PATHS.subscriptionRestartReferral);
			}
		} catch (error) {
			console.error(error);
			navigate(ROUTE_PATHS.error);
			setSubmitting(false);
		}
	};

	return { handleDriverPageSubmit, submitting };
};
